import { FC } from 'react';
import classNames from 'classnames';
import { CheckIcon } from '@heroicons/react/solid';

export type WidgetStyle = 'card' | 'inline';

interface WidgetStylePickerProps {
  selected: WidgetStyle;
  onChange: (style: WidgetStyle) => void;
}

const WidgetStylePicker: FC<WidgetStylePickerProps> = ({
  selected,
  onChange,
}) => {
  return (
    <div>
      <div className="mb-1 text-caption font-medium text-green-150">Style</div>
      <div className="mb-3 text-caption text-neutral-125">
        Choose how the widget appears on your website.
      </div>
      <div className="flex gap-3">
        <StyleOption
          label="Card"
          value="card"
          selected={selected === 'card'}
          onClick={() => onChange('card')}
        >
          {/* Card thumbnail */}
          <div className="flex flex-col items-center gap-1.5 p-3">
            <div className="h-5 w-5 rounded bg-neutral-125" />
            <div className="h-1.5 w-3/5 rounded-full bg-neutral-100" />
            <div className="mt-1 h-6 w-full rounded bg-neutral-75" />
            <div className="h-6 w-full rounded bg-neutral-125" />
          </div>
        </StyleOption>

        <StyleOption
          label="Inline"
          value="inline"
          selected={selected === 'inline'}
          onClick={() => onChange('inline')}
        >
          {/* Inline thumbnail */}
          <div className="flex flex-col gap-1.5 p-3">
            <div className="h-1.5 w-4/5 rounded-full bg-neutral-100" />
            <div className="h-1.5 w-3/5 rounded-full bg-neutral-75" />
            <div className="mt-1 flex gap-1">
              <div className="h-6 flex-1 rounded bg-neutral-75" />
              <div className="h-6 w-10 rounded bg-neutral-125" />
            </div>
          </div>
        </StyleOption>
      </div>
    </div>
  );
};

const StyleOption: FC<{
  label: string;
  value: string;
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, selected, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      'relative flex flex-1 cursor-pointer flex-col rounded-lg border-2 transition-colors',
      selected
        ? 'border-neutral-125'
        : 'border-neutral-75 hover:border-neutral-100',
    )}
  >
    {selected && (
      <div className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-neutral-125">
        <CheckIcon className="h-3 w-3 text-white" />
      </div>
    )}
    <div className="flex-1 rounded-t-md bg-neutral-30">{children}</div>
    <div className="py-2 text-center text-small-caption font-medium text-neutral-150">
      {label}
    </div>
  </button>
);

export default WidgetStylePicker;
